// Organizations admin page - organisations that own power points + booking rows.
// Platform-admin only (the entire Management group is). Single-page CRUD
// with a modal for new/edit, modeled on the installation-parties page.
import { api } from '../api';
import { initAdminShell, escapeHtml, showError, hideError, confirmModal, setButtonBusy } from './shell';
import { t } from '../i18n/i18n';

interface Organization {
  id: string;
  name: string;
  contactEmail: string | null;
  country: string;
  createdAt: string;
}

const ICON_EDIT = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>';
const ICON_TRASH = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/></svg>';

(async () => {
  const me = await initAdminShell('organizations').catch(() => null);
  if (!me) return;

  const tbody = document.getElementById('owners-tbody');
  if (!tbody) return;

  // ---- Modal state ----
  const overlay     = document.getElementById('owner-modal-overlay') as HTMLElement | null;
  const modalTitle  = document.getElementById('owner-modal-title') as HTMLElement | null;
  const form        = document.getElementById('owner-form') as HTMLFormElement | null;
  const errorBox    = document.getElementById('owner-modal-error') as HTMLElement | null;
  const closeBtn    = document.getElementById('owner-modal-close');
  const cancelBtn   = document.getElementById('owner-modal-cancel');
  const submitBtn   = document.getElementById('owner-modal-submit') as HTMLButtonElement | null;
  const idInput     = document.getElementById('owner-id') as HTMLInputElement | null;
  const nameInput   = document.getElementById('owner-name') as HTMLInputElement | null;
  const emailInput  = document.getElementById('owner-email') as HTMLInputElement | null;
  const countryInput = document.getElementById('owner-country') as HTMLInputElement | null;

  let editingId: string | null = null;

  function openModal(organization?: Organization): void {
    if (!overlay || !form) return;
    editingId = organization?.id ?? null;
    if (modalTitle) modalTitle.textContent = t(organization ? 'admin.organizations.title_edit' : 'admin.organizations.title_new');
    if (idInput) {
      idInput.value = organization?.id ?? '';
      // ID is the primary key - locked once the row exists.
      idInput.disabled = organization != null;
    }
    if (nameInput)    nameInput.value    = organization?.name         ?? '';
    if (emailInput)   emailInput.value   = organization?.contactEmail ?? '';
    if (countryInput) countryInput.value = organization?.country      ?? '';
    if (errorBox) { errorBox.style.display = 'none'; errorBox.textContent = ''; }
    overlay.classList.add('is-open');
    overlay.removeAttribute('aria-hidden');
    document.body.style.overflow = 'hidden';
    (organization ? nameInput : idInput)?.focus();
  }

  function closeModal(): void {
    if (!overlay) return;
    overlay.classList.remove('is-open');
    overlay.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    editingId = null;
  }

  function showModalError(msg: string): void {
    if (!errorBox) return;
    errorBox.textContent = msg;
    errorBox.style.display = 'block';
  }

  closeBtn?.addEventListener('click', closeModal);
  cancelBtn?.addEventListener('click', closeModal);
  overlay?.addEventListener('click', (e) => { if (e.target === overlay) closeModal(); });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && overlay?.classList.contains('is-open')) closeModal(); });

  document.getElementById('add-owner-btn')?.addEventListener('click', () => openModal());

  // ---- Submit ----
  submitBtn?.addEventListener('click', async () => {
    if (!form?.reportValidity()) return;

    const id           = idInput?.value.trim().toLowerCase() ?? '';
    const name         = nameInput?.value.trim() ?? '';
    const contactEmail = emailInput?.value.trim() || null;
    const country      = countryInput?.value.trim().toUpperCase() ?? '';

    if (!name)    { showModalError(t('admin.organizations.err_name')); return; }
    if (!country) { showModalError(t('admin.organizations.err_country')); return; }
    if (editingId == null && !id) { showModalError(t('admin.organizations.err_id')); return; }

    setButtonBusy(submitBtn, true);
    try {
      const client = await api();
      if (editingId == null) {
        await client.post('/api/admin/organizations', { id, name, contactEmail, country });
      } else {
        await client.put(`/api/admin/organizations/${encodeURIComponent(editingId)}`, { name, contactEmail, country });
      }
      closeModal();
      await load();
    } catch (err) {
      const status = (err as { response?: { status?: number; data?: { message?: string } } })?.response?.status;
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      if (status === 409) {
        showModalError(msg ?? t('admin.organizations.err_duplicate'));
      } else {
        showModalError(msg ?? (err instanceof Error ? err.message : t('admin.organizations.err_save')));
      }
    } finally {
      setButtonBusy(submitBtn, false);
    }
  });

  // ---- Render ----

  function render(organizations: Organization[]): void {
    if (!tbody) return;
    if (organizations.length === 0) {
      tbody.innerHTML = `<tr><td colspan="6" class="req-empty">${escapeHtml(t('admin.organizations.empty'))}</td></tr>`;
      return;
    }
    const editLabel = t('admin.actions.edit');
    const deleteLabel = t('admin.actions.delete');
    tbody.innerHTML = organizations.map((o) => {
      const created = o.createdAt ? new Date(o.createdAt).toLocaleDateString() : '';
      const email = o.contactEmail
        ? `<a href="mailto:${escapeHtml(o.contactEmail)}">${escapeHtml(o.contactEmail)}</a>`
        : '';
      return `
        <tr>
          <td><code>${escapeHtml(o.id)}</code></td>
          <td>${escapeHtml(o.name)}</td>
          <td>${email}</td>
          <td>${escapeHtml(o.country)}</td>
          <td>${escapeHtml(created)}</td>
          <td class="row-actions">
            <button type="button" class="row-action" data-action="edit" data-id="${escapeHtml(o.id)}">${ICON_EDIT}<span>${escapeHtml(editLabel)}</span></button>
            <button type="button" class="row-action row-action--danger" data-action="delete" data-id="${escapeHtml(o.id)}" data-label="${escapeHtml(o.name)}">${ICON_TRASH}<span>${escapeHtml(deleteLabel)}</span></button>
          </td>
        </tr>`;
    }).join('');

    tbody.querySelectorAll<HTMLButtonElement>('button[data-action="edit"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const id = btn.dataset.id ?? '';
        const organization = organizations.find((o) => o.id === id);
        if (organization) openModal(organization);
      });
    });
    tbody.querySelectorAll<HTMLButtonElement>('button[data-action="delete"]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const id = btn.dataset.id ?? '';
        const label = btn.dataset.label ?? id;
        if (!(await confirmModal({
          title: t('admin.confirm.modal_title_delete'),
          message: t('admin.organizations.delete_confirm').replace('{label}', label),
          confirmLabel: t('admin.confirm.modal_confirm_delete'),
        }))) return;
        setButtonBusy(btn, true);
        try {
          const client = await api();
          await client.delete(`/api/admin/organizations/${encodeURIComponent(id)}`);
          await load();
        } catch (err) {
          const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
          showError('owners-error', msg ?? (err instanceof Error ? err.message : t('admin.organizations.err_delete')));
          setButtonBusy(btn, false);
        }
      });
    });
  }

  async function load(): Promise<void> {
    hideError('owners-error');
    try {
      const client = await api();
      const { data } = await client.get<Organization[]>('/api/admin/organizations');
      render(data);
    } catch (err) {
      showError('owners-error', err instanceof Error ? err.message : t('admin.organizations.err_load'));
    }
  }

  await load();
})();
